import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Grid} from '@mui/material';
import {Concept} from 'types';
import type {
  ColumnSet,
  ColumnWithLabel,
  ViewMode,
} from 'services/advancedSearch/types';
import {useLocalization} from 'localization';
import {
  FieldLabel,
  GroupItemNameValue,
  Icon,
  type LabelValue,
  Layout,
  Select,
  Spacer,
} from 'components';
import {mapColumnSetToOption} from '../mappers/mapColumnSetToOption';
import {ColumnCheckbox} from './ColumnCheckbox';

type Props = {
  /**
   * Limits some options if viewMode is work.
   */
  viewMode: ViewMode;
  initialColumns: string[];
  allColumnSets: ColumnSet[];
  allColumns: ColumnWithLabel[];
  onChange: (columns: string[]) => void;
};

export const ColumnPicker: React.FC<Props> = ({
  viewMode,
  initialColumns,
  allColumnSets,
  allColumns,
  onChange,
}) => {
  const {t} = useLocalization();

  const {
    CustomColumnSetOption,
    AllColumnSetOption,
  }: {
    CustomColumnSetOption: LabelValue<string>;
    AllColumnSetOption: LabelValue<string>;
  } = useMemo(() => {
    return {
      CustomColumnSetOption: {
        value: 'custom',
        label: t('data.column.custom'),
      },
      AllColumnSetOption: {
        value: 'allColumns',
        label: t('data.column.all'),
      },
    };
  }, [t]);

  const [selectedColumnSetId, setSelectedColumnSetId] = useState<string>(
    CustomColumnSetOption.value,
  );
  const [selectedColumns, setSelectedColumns] =
    useState<string[]>(initialColumns);
  const [showColumnCheckboxes, setShowColumnCheckboxes] =
    useState<boolean>(true);

  const columnSetOptions: ReadonlyArray<LabelValue<string>> = useMemo(() => {
    if (!allColumnSets.length) {
      return [CustomColumnSetOption];
    }

    // Adds a Group item between each private/public-block.
    const privateOptions = allColumnSets
      .filter(c => !c.isPublic)
      .map(mapColumnSetToOption);

    if (privateOptions.length > 0) {
      privateOptions.unshift({
        value: GroupItemNameValue,
        label: t('data.field.private.label'),
      });
    }

    const publicOptions = allColumnSets
      .filter(c => c.isPublic)
      .map(mapColumnSetToOption);

    if (publicOptions.length > 0) {
      publicOptions.unshift({
        value: GroupItemNameValue,
        label: t('data.field.public.label'),
      });
    }
    return [
      CustomColumnSetOption,
      AllColumnSetOption,
      ...privateOptions,
      ...publicOptions,
    ];
  }, [AllColumnSetOption, CustomColumnSetOption, allColumnSets, t]);

  const handleToggleSelectedColumn = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      const columnName = event.target.name;
      if (!checked) {
        setSelectedColumns(selectedColumns.filter(c => c !== columnName));
      } else {
        setSelectedColumns([...selectedColumns, columnName]);
      }
      setSelectedColumnSetId(CustomColumnSetOption.value);
    },
    [CustomColumnSetOption.value, selectedColumns],
  );

  const RenderColumnCheckboxes = useMemo(() => {
    const selected = allColumns.filter(a => selectedColumns.includes(a.name));
    const unselected = allColumns.filter(
      a => !selectedColumns.includes(a.name),
    );

    return (
      [
        ...selected.sort((a, b) => {
          return a.label > b.label ? 1 : -1;
        }),
        ...unselected.sort((a, b) => {
          return a.label > b.label ? 1 : -1;
        }),
      ]
        // If viewMode is work, only show columns that are marked as workColumn
        .filter(a => {
          if (viewMode === Concept.work) {
            return a.workColumn;
          }
          return true;
        })
        .map(column => (
          <Grid key={column.name} size={{xs: 6, sm: 4, md: 3}}>
            <ColumnCheckbox
              name={column.name}
              label={column.label}
              checked={selectedColumns.includes(column.name)}
              onChange={handleToggleSelectedColumn}
            />
          </Grid>
        ))
    );
  }, [allColumns, handleToggleSelectedColumn, selectedColumns, viewMode]);

  const handleSetSelectedColumnSetId = useCallback(
    (value: string | {value: string}) => {
      const val = typeof value === 'string' ? value : value.value;

      if (val === CustomColumnSetOption.value) {
        // None
        setSelectedColumnSetId(val);
        setSelectedColumns([]);
      } else if (val === AllColumnSetOption.value) {
        // All
        setSelectedColumnSetId(val);
        setSelectedColumns(allColumns.map(c => c.name));
      } else {
        // Selected
        const columnSet = allColumnSets.find(a => a.id === val);
        if (columnSet) {
          setSelectedColumnSetId(val);
          setSelectedColumns(columnSet.columns);
        }
      }
    },
    [
      AllColumnSetOption.value,
      CustomColumnSetOption.value,
      allColumnSets,
      allColumns,
    ],
  );

  useEffect(() => {
    onChange(selectedColumns);
  }, [onChange, selectedColumns]);

  return (
    <>
      <FieldLabel label={t('page.search.filter.collection.label')} />
      <Select
        value={selectedColumnSetId}
        options={columnSetOptions}
        onChange={handleSetSelectedColumnSetId}
      />
      <Spacer height={2} />
      <Layout>
        <Button
          variant={'text'}
          size={'large'}
          onClick={() => setShowColumnCheckboxes(!showColumnCheckboxes)}
          startIcon={
            <Icon icon={showColumnCheckboxes ? 'ChevronUp' : 'DropDown'} />
          }>
          {showColumnCheckboxes ? 'Skjul enkeltkolonner' : 'Vis enkeltkolonner'}
        </Button>
      </Layout>
      <Spacer height={1} />
      {showColumnCheckboxes ? (
        <Box
          component={Grid}
          container
          spacing={1}
          sx={{
            padding: 0,
            flex: 1,
            flexDirection: 'row',
            maxWidth: 'md',
          }}>
          {RenderColumnCheckboxes}
        </Box>
      ) : null}
    </>
  );
};
